import { useMemo } from "react";
import { map } from "lodash";
import { useCurrentUser } from "@linkfluence/core/user/use-cases";
import { useProject } from "@radarly/core/project/context";
import { useWorkspace } from "@radarly/core/workspace/context";
import { getWizardConfigurationClauseGenerator } from "@radarly/models/insight-pages/wizard/configuration";
import { WizardGeneratorFactory } from "@radarly/models/insight-pages/wizard/generator";
import {
  WizardTemplate,
  WizardTemplateSection,
} from "@radarly/models/insight-pages/wizard/model";
import { WizardConfiguration } from "@radarly/models/insight-pages/wizard/model/configuration";

export function useWizardGenerator() {
  const project = useProject();
  const workspace = useWorkspace();
  const user = useCurrentUser();
  const generator = useMemo(() => {
    const generator = WizardGeneratorFactory.getInstance();
    if (project) {
      generator.setProjectId(project.id);
      generator.setProjectFocuses(map(project.focuses, "id"));
    }
    if (workspace) {
      generator.setWorkspaceId(workspace.id);
    }
    if (project?.personIdentifications?.length > 0) {
      generator.setPersonIdentifications(project?.personIdentifications);
    }
    if (user) {
      generator
        .setAuthorId(user.id)
        .setLocale(user.locale)
        .setTimezone(user.timezone);
    }
    return generator;
  }, [project, workspace, user]);
  const generateInsightPageRowsFromSection = (
    section: WizardTemplateSection,
    options?: Omit<WizardConfiguration, "title">
  ) => {
    if (options && options.locale) {
      generator.setLocale(options.locale);
    }
    if (options && options.timezone) {
      generator.setTimezone(options.timezone);
    }
    if (options && options.period) {
      generator.setPeriod(options.period);
    }
    if (options && options.description) {
      generator.setDescription(options.description);
    }
    if (options && options.includeAIAnalysis !== undefined) {
      generator.setIncludeAIAnalysis(options.includeAIAnalysis);
    }
    if (options && options.aiContext) {
      generator.setAIContext(options.aiContext);
    }
    if (options && options.fields) {
      generator.setClauses(
        getWizardConfigurationClauseGenerator(options.fields)
      );
      generator.setLoopItems({
        queries: options.fields.queries,
      });
    }
    return generator.generateInsightPageRowsFromSection(section, options, true);
  };
  const generateInsightPage = (
    template: WizardTemplate,
    options?: Partial<WizardConfiguration>
  ) => {
    if (options && options.locale) {
      generator.setLocale(options.locale);
    }
    if (options && options.timezone) {
      generator.setTimezone(options.timezone);
    }
    if (options && options.period) {
      generator.setPeriod(options.period);
    }
    if (options && options.description) {
      generator.setDescription(options.description);
    }
    if (options && options.includeAIAnalysis !== undefined) {
      generator.setIncludeAIAnalysis(options.includeAIAnalysis);
    }
    if (options && options.aiContext) {
      generator.setAIContext(options.aiContext);
    }
    if (options && options.fields) {
      generator.setClauses(
        getWizardConfigurationClauseGenerator(options.fields)
      );
      generator.setLoopItems({
        queries: options.fields.queries,
      });
    }
    return generator.generateInsightPage(template, options);
  };
  return {
    generateInsightPage,
    generateInsightPageRowsFromSection,
  };
}
